import { z } from 'zod';

/**
 * The vocabularies for the member contact case — the "needs contact" obligation.
 *
 * There is exactly ONE case type. A member who wrote and a newly assigned member
 * awaiting a welcome are the same obligation: one care-team message satisfies
 * either. So they are one case with two OPENING reasons, not two cases.
 *
 * This deliberately does not port Pelago 1's `ChatStatus`, which conflated three
 * incompatible things in one enum — a computed predicate (`UNANSWERED` /
 * `ANSWERED`), a human declaration ("Mark as answered", which existed precisely
 * because a predicate has no exit), and an automation-owned state
 * (`AUTO_PENDING`, owned by Iterable). A case has an owner, an opening event and
 * an exit, which is what the predicate could not express.
 *
 * Opening and closing are two vocabularies rather than one four-member `reason`
 * enum: they answer different questions, and a single column would make "closed
 * because they were assigned" representable.
 *
 * Both tuples are also the STORAGE vocabulary — they feed the
 * `member_contact_cases` CHECK constraints via `enumCheck`, where every other
 * governed enum in the schema is snake_case.
 */

/**
 * Why the obligation began.
 *
 * `member_message` — the member wrote and nobody on the care team has answered.
 * `assigned` — a member was newly assigned and is waiting on a first contact.
 * `staff_assigned` — a member of staff made the member's first primary
 * assignment by hand, rather than an automatic writer.
 *
 * The reason is recorded once and never rewritten: a member who is assigned and
 * then writes before anyone replies keeps `assigned`, because the obligation is
 * the same one and it began at the assignment.
 */
export const MEMBER_CONTACT_CASE_OPENED_REASON = [
  'member_message',
  'assigned',
  'staff_assigned',
] as const;
export const memberContactCaseOpenedReasonSchema = z.enum(MEMBER_CONTACT_CASE_OPENED_REASON);
export type MemberContactCaseOpenedReason = z.infer<typeof memberContactCaseOpenedReasonSchema>;

/**
 * How the obligation ended.
 *
 * `replied` — someone on the care team sent the member a message.
 * `no_reply_needed` — a care person decided no contact is owed.
 *
 * The second member is the reason this is a case rather than a predicate: a
 * computed "unanswered" state has no exit that does not involve sending a
 * message, which is why Pelago 1 needed a "Mark as answered" button bolted onto
 * an enum that could not represent the act.
 */
export const MEMBER_CONTACT_CASE_CLOSED_REASON = ['replied', 'no_reply_needed'] as const;
export const memberContactCaseClosedReasonSchema = z.enum(MEMBER_CONTACT_CASE_CLOSED_REASON);
export type MemberContactCaseClosedReason = z.infer<typeof memberContactCaseClosedReasonSchema>;
